import dynamic from 'next/dynamic';

import { cn } from '@natu/utils/cn';

import { type ResponsiveImageProps } from '../ResponsiveImage/ResponsiveImage';
import type { TypographyVariantProp } from '../Typography/Typography.type';

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

const Typography = dynamic(() => import('@natu/ui/Typography').then(mod => mod.Typography));

const variantsProps = {
  blog: {
    wrapper: { className: 'flex gap-4 items-center' },
    image: {
      className: 'size-14 shrink-0 rounded-full overflow-hidden',
      sizes: '56px',
    },
    name: {
      variant: ['text-sm'] as TypographyVariantProp,
      className: 'font-bold text-gray-900',
    },
    role: {
      variant: ['text-sm'] as TypographyVariantProp,
      className: 'font-medium text-gray-400',
    },
  },
  hero: {
    wrapper: { className: 'flex gap-4 items-center' },
    image: {
      className: 'size-14 shrink-0 rounded-full overflow-hidden lg:hidden',
      sizes: '56px',
    },
    name: {
      variant: ['text-md'] as TypographyVariantProp,
      className: 'font-bold text-gray-700',
    },
    role: {
      variant: ['text-sm'] as TypographyVariantProp,
      className: 'font-medium text-gray-500',
    },
  },
  testimonial: {
    wrapper: { className: 'flex gap-4 items-center' },
    image: {
      className: 'size-14 shrink-0 rounded-full overflow-hidden',
      sizes: '56px',
    },
    name: {
      variant: ['text-lg', 'text-md'] as TypographyVariantProp,
      className: 'font-bold text-gray-700',
    },
    role: {
      variant: ['text-sm', 'text-xs'] as TypographyVariantProp,
      className: 'font-medium text-gray-500',
    },
  },
  white: {
    wrapper: { className: 'flex gap-4 items-center' },
    image: {
      className: '',
      sizes: '',
    },
    name: {
      variant: ['text-lg'] as TypographyVariantProp,
      className: 'font-bold text-white',
    },
    role: {
      variant: ['text-md'] as TypographyVariantProp,
      className: 'font-medium text-primary-100',
    },
  },
  contact: {
    wrapper: { className: 'mb-8 flex items-center gap-x-4' },
    image: {
      className: 'size-14 shrink-0 object-cover',
      sizes: '56px',
    },
    name: {
      variant: ['text-lg'] as TypographyVariantProp,
      className: 'font-semibold text-white',
    },
    role: {
      variant: ['text-md'] as TypographyVariantProp,
      className: 'text-white',
    },
  },
  careersSmall: {
    wrapper: { className: 'flex items-center gap-x-4' },
    image: {
      className: 'size-14 shrink-0 object-cover',
      sizes: '56px',
    },
    name: {
      variant: ['text-md'] as TypographyVariantProp,
      className: 'font-bold text-gray-700',
    },
    role: {
      variant: ['text-xs'] as TypographyVariantProp,
      className: 'font-medium text-gray-500',
    },
  },
  person: {
    wrapper: { className: 'flex flex-col items-center' },
    image: {
      className: 'mb-5 size-24 shrink-0 rounded-full object-cover',
      sizes: '96px',
    },
    name: {
      variant: ['text-md', 'text-md', 'text-lg'] as TypographyVariantProp,
      className: 'text-center font-semibold -tracking-[0.2px] text-gray-900',
    },
    role: {
      variant: ['text-sm', 'text-sm', 'text-md'] as TypographyVariantProp,
      className: 'text-center -tracking-[0.004em] text-gray-400',
    },
  },
  quote: {
    wrapper: { className: 'flex items-center gap-x-4' },
    image: {
      className: 'size-16 shrink-0 rounded-full object-cover',
      sizes: '64px',
    },
    name: {
      variant: ['text-sm', 'text-md'] as TypographyVariantProp,
      className: 'text-naturaily-prose',
    },
    role: {
      variant: ['text-sm', 'text-md'] as TypographyVariantProp,
      className: 'text-gray-400',
    },
  },
};

type AuthorImage = {
  src?: string | null;
  alt?: string | null;
  title?: string | null;
};

export interface AuthorProps {
  className?: string;
  priority?: boolean;
  image?: AuthorImage;
  name?: string;
  role?: string;
  variant?: keyof typeof variantsProps;
}

const Author = ({
  className,
  image,
  priority,
  name,
  role,
  variant = 'testimonial',
  ...rest
}: AuthorProps) => {
  const variantProps = variantsProps[variant];

  return (
    <div className={cn(variantProps.wrapper.className, className)} {...rest}>
      {image?.src && (
        <ResponsiveImage
          className={variantProps.image.className}
          priority={priority}
          {...(image as ResponsiveImageProps)}
          sizes={variantProps.image.sizes}
        />
      )}
      <div>
        {name && <Typography {...variantProps.name}>{name}</Typography>}
        {role && <Typography {...variantProps.role}>{role}</Typography>}
      </div>
    </div>
  );
};

export { Author };
